import React, { useState, useContext, useEffect, useRef } from 'react';
import { trackEvent } from "../../utils/google-analytics";

import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import "../../styles/ComponentStyles/Footer.css";
import logoImage from "../../assets/images/LogoCombatSolutionsWhite.png";
import fallbackLogo from "../../assets/images/LogoCombatSolutionsBlack.png";
import { createSubscription } from "../../services/subscription.service";
import { ModalContext } from "../../context/ModalContext";

const Footer = () => {
  const footerRef = useRef(null);
  const logoRef = useRef(null);
  const columnRefs = useRef([]);
  const socialRefs = useRef([]);
  const bottomRef = useRef(null);

  const { showToast } = useContext(ModalContext);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          trackEvent("footer_view", {
            page: "Global Footer",
          });
          if (entry.isIntersecting) {
            if (logoRef.current) {
              logoRef.current.style.opacity = "0";
              logoRef.current.style.transform = "translateY(30px)";
              setTimeout(() => {
                logoRef.current.style.transition = "all 0.8s ease-out";
                logoRef.current.style.opacity = "1";
                logoRef.current.style.transform = "translateY(0)";
              }, 100);
            }

            columnRefs.current.forEach((col, index) => {
              if (col) {
                col.style.opacity = "0";
                col.style.transform = "translateY(30px)";
                setTimeout(() => {
                  col.style.transition = "all 0.8s ease-out";
                  col.style.opacity = "1";
                  col.style.transform = "translateY(0)";
                }, 200 + index * 100);
              }
            });

            socialRefs.current.forEach((social, index) => {
              if (social) {
                social.style.opacity = "0";
                social.style.transform = "scale(0)";
                setTimeout(() => {
                  social.style.transition =
                    "all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)";
                  social.style.opacity = "1";
                  social.style.transform = "scale(1)";
                }, 400 + index * 100);
              }
            });

            if (bottomRef.current) {
              bottomRef.current.style.opacity = "0";
              setTimeout(() => {
                bottomRef.current.style.transition = "all 0.8s ease-out";
                bottomRef.current.style.opacity = "1";
              }, 800);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const addToRefs = (el, refsArray) => {
    if (el && !refsArray.current.includes(el)) {
      refsArray.current.push(el);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast(
        <>
          <i className="bi bi-exclamation-triangle" /> Please enter your email
        </>
      );
      return;
    }

    const payload = {
      subscription_email: email.trim(),
    };

    try {
      setLoading(true);
      await createSubscription(payload);

      showToast(
        <>
          <i className="bi bi-check2-all" /> Subscribed successfully!
        </>
      );

      setEmail("");
    } catch (error) {
      console.error("Subscription Error:", error);

      showToast(
        <>
          <i className="bi bi-x-circle" /> Subscription failed
        </>
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer ref={footerRef} className="combat-footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Brand */}
          <div ref={logoRef} className="footer-brand">
            <div className="brand-logo">
              <div className="footer-logo-icon">
                <img
                  src={logoImage}
                  alt="Combat Solutions"
                  loading="lazy"
                  onError={(e) => {
                    // If original image fails to load, replace with an inline SVG fallback
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="180" height="36"><rect width="180" height="36" fill="%23000" opacity="0"/><text x="0" y="18" fill="%23ff6b00" font-family="Arial, Helvetica, sans-serif" font-size="16">Combat Solutions</text></svg>';
                  }}
                />
              </div>
              <h2 className="brand-name">
                <span className="brand-combat">Combat Solutions</span>
                <span> AI</span>
              </h2>
            </div>

            <p className="brand-description">
              Engineering smart systems powered by Generative AI for enterprises
              and regulated industries worldwide.
            </p>

            {/* Newsletter */}
            <div className="newsletter">
              <p className="newsletter-text">
                Stay updated with AI insights & product releases
              </p>

              <form className="newsletter-form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  className="newsletter-input"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  className="newsletter-button"
                  disabled={loading}
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            </div>

            {/* Social */}
            <div className="social-links">
              <a
                ref={(el) => addToRefs(el, socialRefs)}
                href="https://www.linkedin.com/company/combat-solutions-ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="LinkedIn"
                onClick={() =>
                  trackEvent("footer_social_click", {
                    page: "Global Footer",
                    social: "LinkedIn",
                  })
                }
              >
                <i className="bi bi-linkedin"></i>
              </a>

              <a
                ref={(el) => addToRefs(el, socialRefs)}
                href="https://www.instagram.com/combatsolutionai/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
              >
                <i className="bi bi-instagram"></i>
              </a>

              <a
                ref={(el) => addToRefs(el, socialRefs)}
                href="https://x.com/CombatSolAI"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
              >
                <i className="bi bi-twitter-x"></i>
              </a>

              <a
                ref={(el) => addToRefs(el, socialRefs)}
                href="https://www.facebook.com/CombatSolutionsAI/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
              >
                <i className="bi bi-facebook"></i>
              </a>
            </div>
          </div>

          {/* Company */}
          <div ref={(el) => addToRefs(el, columnRefs)} className="footer-column">
            <h3 className="column-title">Company</h3>
            <ul className="column-links">
              <li>
                <Link
                  to="/about-us"
                  onClick={() =>
                    trackEvent("footer_link_click", {
                      page: "Global Footer",
                      link: "About Us",
                    })
                  }
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/career"
                  onClick={() =>
                    trackEvent("footer_link_click", {
                      page: "Global Footer",
                      link: "careers",
                    })
                  }
                >
                  careers
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  onClick={() =>
                    trackEvent("footer_link_click", {
                      page: "Global Footer",
                      link: "Contact Us",
                    })
                  }
                >
                  Contact Us
                </Link>
              </li>


            </ul>
          </div>

          {/* Expertise */}
          <div ref={(el) => addToRefs(el, columnRefs)} className="footer-column">
            <h3 className="column-title">Expertise</h3>
            <ul className="column-links">
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/products">Products</Link></li>
              <li><Link to="/technologies">Technologies</Link></li>
            </ul>
          </div>

          {/* Locations */}
          <div ref={(el) => addToRefs(el, columnRefs)} className="footer-column">
            <h3 className="column-title">Global Presence</h3>
            <ul className="location-list">
              <li className="location-item">
                <MapPin size={16} />
                <div>
                  <div className="location-name">Nashik, India</div>
                  <div className="location-desc">Engineering & Delivery</div>
                </div>
              </li>
              <li className="location-item">
                <MapPin size={16} />
                <div>
                  <div className="location-name">Mumbai, India</div>
                  <div className="location-desc">Enterprise & Defence</div>
                </div>
              </li>
              <li className="location-item">
                <MapPin size={16} />
                <div>
                  <div className="location-name">Sydney, Australia</div>
                  <div className="location-desc">Global Operations</div>
                  <a
                    href="https://adainn.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="location-link"
                    
                  >
                     adainn.com ↗
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div ref={bottomRef} className="footer-bottom">
          <div className="footer-bottom-content">
            <p>© 2026 Combat Solutions. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/Privacy">Privacy Policy</Link>
              <Link to="/Terms">Terms of Service</Link>
              <Link to="/contact" className="cta-link">Contact Us</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
